import { addBusinessDays, differenceInBusinessDays } from 'date-fns'
import { DateRange } from 'react-day-picker'
import { DropdownOption } from '../../atoms'
import { Holiday, isHoliday, validBusinessDay } from '../../atoms/at-date-picker/overrides'
import { PriceValue } from './components'
import { FormFieldState } from './index'
import { SpaceType } from '../or-filter-section/configurations'
import { formatCurrency } from '../../utils'

interface BookingPricingValue {
  total: PriceValue
  reservation: PriceValue & {
    rawFee?: number
    rawDiscountedFee?: number
    discountCode?: string
  }
}

export const bookingPricing = (
  unitPrice: number,
  availableDiscounts: number[],
  state: FormFieldState,
  spaceType: SpaceType,
  holidays: Holiday | undefined
): BookingPricingValue => {
  const { from, to } = (state.date?.value as DateRange) ?? { from: validBusinessDay(new Date()) }
  const desks = 'desks' in state ? parseInt((state.desks.value as DropdownOption).value) : 0
  const duration = 'duration' in state ? parseFloat((state.duration.value as DropdownOption).value) : undefined
  const [discount1 = 0, discount2 = 0] = availableDiscounts
  let reservationLabel = ''
  let discountCode = ''
  let fee = 0
  let discountedFee = 0

  if (from) {
    const daysUnfiltered = to ? Math.abs(differenceInBusinessDays(from, to)) + 1 : 1
    const days = Array.from({ length: daysUnfiltered }, (_, i) => addBusinessDays(from, i)).reduce(
      (acc, date) => (!isHoliday(date, holidays) ? acc + 1 : acc),
      0
    )

    if (desks) {
      fee = unitPrice * days * desks

      if ((discount1 || discount2) && (days >= 5 || days >= 20)) {
        const [currentDiscount, discountDays] = days >= 20 ? [discount2, 20] : [discount1, 5]
        discountCode = `${currentDiscount}% off for ${discountDays}+ days`
        discountedFee = fee - (fee * currentDiscount) / 100
      }

      reservationLabel = `${formatCurrency(unitPrice)} x ${days} ${days === 1 ? 'day' : 'days'} x ${desks} ${
        desks === 1 ? 'day pass' : 'day passes'
      }`
    } else if (duration && !to) {
      fee = unitPrice * duration

      if ((discount1 || discount2) && (duration >= 4 || duration >= 8)) {
        const [currentDiscount, discountHours] = duration >= 8 ? [discount2, 8] : [discount1, 4]
        discountCode = `${currentDiscount}% off for ${discountHours}+ hours`
        discountedFee = fee - (fee * currentDiscount) / 100
      }

      reservationLabel = `${formatCurrency(unitPrice)} x ${duration} ${duration === 1 ? 'hour' : 'hours'}`
    } else if (spaceType === 'office') {
      fee = unitPrice * days
      if ((discount1 || discount2) && (days >= 5 || days >= 20)) {
        const [currentDiscount, discountDays] = days >= 20 ? [discount2, 20] : [discount1, 5]
        discountCode = `${currentDiscount}% off for ${discountDays}+ days`
        discountedFee = fee - (fee * currentDiscount) / 100
      }
      reservationLabel = `${formatCurrency(unitPrice)} x ${days} ${days === 1 ? 'day' : 'day(s)'}`
    } else {
      const totalHours = days * 8 // 8 hours per day
      fee = unitPrice * totalHours

      if ((discount1 || discount2) && (days >= 5 || days >= 20 || 'startTime' in state)) {
        const [currentDiscount, discountHours] = days >= 20 || 'startTime' in state ? [discount2, 8] : [discount1, 4]
        discountCode = `${currentDiscount}% off for ${discountHours}+ hours`
        discountedFee = fee - (fee * currentDiscount) / 100
      }
      reservationLabel = `${formatCurrency(unitPrice)}/hour x ${days} ${days === 1 ? 'day' : 'day(s)'}`
    }
  }

  const value = formatCurrency(fee)
  const discountedValue = discountedFee ? formatCurrency(discountedFee) : null

  return {
    total: {
      label: 'Total before taxes',
      fee: discountedValue ?? value,
    },
    reservation: {
      label: reservationLabel,
      fee: value,
      rawFee: fee,
      ...(discountedValue ? { discountedFee: discountedValue, rawDiscountedFee: discountedFee, discountCode } : {}),
    },
  }
}
